import { useState, useContext } from "react";
import { ButtonState } from "@jcharlesworthuk/your-mum-frontend/models";
import * as Sentry from '@sentry/browser';
import { isNullOrEmpty, generateNewId, isEmailAddress } from "@jcharlesworthuk/your-mum-core/dist/functions";
import { EmployeeManagerContext, EmployeeManagerActionType, OrganisationManagerContext, OrganisationManagerActionType, LoggedInContext } from "@Contexts";
import { Employee } from "@Models/Employee";
import { isNumber } from "util";
import { PrivateApiClient } from "../ApiClient";
import { EmployeeStatus } from "@Models/EmployeeStatus";
import { InputChangeDelegate } from "@jcharlesworthuk/your-mum-frontend/helpers/InputDelegates";
import { v4 as uuidv4 } from 'uuid';
import { Team } from "@Models/Team";
type Fields = {
    teamName: string;
    managerId: string;
}

function isValid(fields: Fields) {
    return !isNullOrEmpty(fields.teamName) && !isNullOrEmpty(fields.managerId);
}

export const useUpsertTeam = (team: Team | null) => {
    const [fields, setFields] = useState<Fields>({
        teamName: team ? team.name : '',
        managerId: team ? team.managerId : '',
    });
    const [buttonState, setButtonState] = useState<ButtonState>(ButtonState.Disabled);
    const [error, setError] = useState<string | 'exists' | undefined>(undefined);
    const { currentUser } = useContext(LoggedInContext);
    const { organisation, dispatch } = useContext(OrganisationManagerContext);

    const registerInputChangeHandler = function (id: string) {
        const handler: InputChangeDelegate = e => {
            const newFields = {
                ...fields,
                [id]: e.currentTarget.value
            };
            setFields(newFields);
            setButtonState(isValid(newFields) ? ButtonState.Enabled : ButtonState.Disabled);
        }
        return handler;
    }


    const handleSubmitForm = (callback: () => void, manager?: Employee) => {
        return (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const fetchDataAction = async () => {
                setButtonState(ButtonState.Working);
                if (manager) {
                    fields.managerId = manager.sub;
                }
                if (!isValid(fields)) {
                    setError('Ensure you have entered all fields');
                    return;
                }
                setError(undefined);
                if (!isValid(fields)) return;
                try {
                    const newTeam = new Team(team ? team.id : generateNewId(), fields.teamName, fields.managerId);
                    const writeModel = Team.createWriteModel(newTeam);
                    const apiClient = new PrivateApiClient().withUser(currentUser);
                    await apiClient.updateOrganisation(writeModel);
                    dispatch({ type: OrganisationManagerActionType.TeamUpserted, team: newTeam, teamId: newTeam.id })

                    if (manager && manager.status === EmployeeStatus.Disabled) {
                        await apiClient.updateEmployee({
                            email: manager.email,
                            userId: manager.sub,
                            status: EmployeeStatus.Active
                        })
                    }

                    setButtonState(ButtonState.Enabled);
                    callback();
                } catch (e) {
                    console.error(e);
                    Sentry.captureException(e);
                    setError(e.message);
                }
            };
            fetchDataAction();
        }
    }

    return {
        fields,
        buttonState,
        registerInputChangeHandler,
        error,
        handleSubmitForm
    };
}